/* eslint-disable react/react-in-jsx-scope */
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Paper, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Typography, Box } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import "../charts.css";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChartInfoIcon from "src/Components/shared/shared/ChartInfoIcon";
import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getUrl } from "src/Utils/Common";
import BrandSales from "src/Components/Charts/ActualVsExpectedMixChart/BrandSales";
import SaveToCanvasButton from "src/Components/shared/shared/SaveToCanvasButton";
import BarChartWob from "src/Components/Charts/ActualVsExpectedMixChart/BarChartWob";
import BarChartWobContribution from "src/Components/Charts/BarChartWobContribution/BarChartWobContribution";
import MarketComparisonTable from "src/Components/Tables/MarketComparison/MarketComparison";
import TwoColumnChart from "src/Components/Charts/TwoColumnChart/TwoColumnChart";
import TwoColumnVolumeChart from "src/Components/Charts/TwoColumnVolumeChart/TwoColumnVolumeChart";
import SourceShareTable from "src/Components/Tables/SourceShareTable/SourceShareTable";
import PriceElasticity from "src/Components/Charts/PriceElasticity/PriceElasticity";
import CumulativeProductShareChart from "src/Components/Charts/CumulativeProductShareChart/CumulativeProductShareChart";
import dayjs from "dayjs";
import PPATable from "src/Components/Tables/PPATable/PPATable";
import MarketKPIsTable from "src/Components/Tables/MarketKPIsTable/MarketKPIsTable";
import NewTable from "src/Components/Tables/MarketGrowthTable/MarketGrowthTable";
import MarketContributionSegments from "src/Components/Charts/MarketContributionSegments/MarketContributionSegments";
import MarketGrowthContribution from "src/Components/Charts/MarketGrowthContribution/MarketGrowthContribution";
import SegmentsRetailerValueSalesChart from "src/Components/Charts/SegmentsRetailerValueSalesChart/SegmentsRetailerValueSalesChart";
import { useRefState } from "src/hooks/useRefState";

const COMPONENT_INFO_MAP = {
  "MarketKPIsTable": MarketKPIsTable,
  "PPATable": PPATable,
  "Brand Sales": BrandSales,
  "Market Comparison": MarketComparisonTable,
  "MarketComparisonTable": MarketComparisonTable,
  "TwoColumnChart": TwoColumnChart,
  "TwoColumnVolumeChart": TwoColumnVolumeChart,
  "SourceShareTable": SourceShareTable,
  "NewTable": NewTable,
  "PriceElasticity": PriceElasticity,
  "CumulativeProductShareChart": CumulativeProductShareChart,
  "BarChartWob": BarChartWob,
  "BarChartWobContribution": BarChartWobContribution,
  "MarketContributionSegments": MarketContributionSegments,
  "MarketGrowthContribution": MarketGrowthContribution,
  "SegmentsRetailerValueSalesChart": SegmentsRetailerValueSalesChart,
};

export const SortableChart = ({
  user,
  RemoveComponent,
  showSaveButton = true,
  showCloseTooltip = true,
  showInsightsButton = true,
}) => {
  const [value, setValue] = useState(user.filters?.value || 0);
  const [isManufacturer, setIsManufacturer] = useState(user.filters?.isManufacturer || false);
  const [selected, setSelected] = useState(user.filters?.selected || { type: "P12M", value: null });
  const [brandSalesTimePeriod, setBrandSalesTimePeriod] = useState(user.filters?.brandSalesTimePeriod || "P12M");
  const [brandSalesSelectedTab, setBrandSalesSelectedTab] = useState(user.filters?.brandSalesSelectedTab || 0);
  const [comparisonType, setComparisonType] = useState(user.filters?.comparisonType || "P12M");
  const [ppaSelectedHier, setPpaSelectedHier] = useState(user.filters?.selectedHier || "price_bracket");
  const [ppaSelectedType, setPpaSelectedType] = useState(user.filters?.selectedType || "value_sales");
  const [menuConfig, setMenuConfig] = useState({});
  const [dropDownValue, setDropDownValue] = useState(user.filters?.dropDownValue || "All Market");

  const [selectedHierarchy, setSelectedHierarchy] = useRefState(
    user.filters?.selectedHierarchy || "class"
  );
  const [twoColumnSalesType, setTwoColumnSalesType, twoColumnSalesTypeRef] = useRefState(
    user.filters?.twoColumnSalesType || null
  );
  const [twoColumnVolumeSalesType, setTwoColumnVolumeSalesType, twoColumnVolumeSalesTypeRef] = useRefState(
    user.filters?.twoColumnVolumeSalesType || null
  );
  const [sourceShareSelectedSku, setSourceShareSelectedSku, sourceShareSelectedSkuRef] = useRefState(
    user.filters?.sourceShareSelectedSku || "SKU1"
  );
  const [priceElasticitySelectedSkus, setPriceElasticitySelectedSkus, priceElasticitySelectedSkusRef] = useRefState(
    user.filters?.priceElasticitySelectedSkus || []
  );
  const [cumulativeProductShareBrand, setCumulativeProductShareBrand, cumulativeProductShareBrandRef] = useRefState(
    user.filters?.cumulativeProductShareBrand || "SAVOLA GROUP"
  );
  const [cumulativeProductShareStore, setCumulativeProductShareStore, cumulativeProductShareStoreRef] = useRefState(
    user.filters?.cumulativeProductShareStore || ""
  );
  const [cumulativeProductShareThreshold, setCumulativeProductShareThreshold, cumulativeProductShareThresholdRef] = useRefState(
    user.filters?.cumulativeProductShareThreshold || 95
  );
  const [marketKPIsSelectedHierarchy, setMarketKPIsSelectedHierarchy, marketKPIsSelectedHierarchyRef] = useRefState(
    user.filters?.marketKPIsSelectedHierarchy || "brand"
  );
  const [marketGrowthSelectedHierarchy, setMarketGrowthSelectedHierarchy, marketGrowthSelectedHierarchyRef] = useRefState(
    user.filters?.marketGrowthSelectedHierarchy || "brand"
  );
  const [marketGrowthStartDate, setMarketGrowthStartDate, marketGrowthStartDateRef] = useRefState(
    user.filters?.marketGrowthStartDate ? dayjs(user.filters.marketGrowthStartDate) : dayjs().subtract(2, "year")
  );
  const [marketGrowthEndDate, setMarketGrowthEndDate, marketGrowthEndDateRef] = useRefState(
    user.filters?.marketGrowthEndDate ? dayjs(user.filters.marketGrowthEndDate) : dayjs().subtract(1, "year")
  );
  const [marketGrowthSelectedTab, setMarketGrowthSelectedTab, marketGrowthSelectedTabRef] = useRefState(
    user.filters?.marketGrowthSelectedTab || 0
  );
  const [marketGrowthTimePeriod, setMarketGrowthTimePeriod, marketGrowthTimePeriodRef] = useRefState(
    user.filters?.marketGrowthTimePeriod || "months"
  );
  const [marketGrowthTarget, setMarketGrowthTarget, marketGrowthTargetRef] = useRefState(
    user.filters?.marketGrowthTarget || 11
  );
  const [marketGrowthContributionSelectedHierarchy, setMarketGrowthContributionSelectedHierarchy, marketGrowthContributionSelectedHierarchyRef] = useRefState(
    user.filters?.marketGrowthContributionSelectedHierarchy || "brand"
  );
  const [segmentsRetailerValueSalesSelectedHierarchy, setSegmentsRetailerValueSalesSelectedHierarchy, segmentsRetailerValueSalesSelectedHierarchyRef] = useRefState(
    user.filters?.segmentsRetailerValueSalesSelectedHierarchy || "brand"
  );
  const [marketContributionSegmentsSelectedHierarchy, setMarketContributionSegmentsSelectedHierarchy, marketContributionSegmentsSelectedHierarchyRef] = useRefState(
    user.filters?.marketContributionSegmentsSelectedHierarchy || "brand"
  );
  const [productAssortmentSelected, setProductAssortmentSelected, productAssortmentSelectedRef] = useRefState(
    user.filters?.productAssortmentSelected || "SAVOLA GROUP"
  );
  const [productAssortmentSelectedVsHier, setProductAssortmentSelectedVsHier, productAssortmentSelectedVsHierRef] = useRefState(
    user.filters?.productAssortmentSelectedVsHier || ""
  );
  const [productAssortmentSelectedVsHierValue, setProductAssortmentSelectedVsHierValue, productAssortmentSelectedVsHierValueRef] = useRefState(
    user.filters?.productAssortmentSelectedVsHierValue || "All"
  );

  const projectId = useSelector((state) => state.userProject.projectId);
  const selectedFilter = useSelector((state) => state.filter.selectedFilter);
  const currentChartType = useSelector((state) => state.chart.currentChartType);
  const currentDateComparison = useSelector((state) => state.dateCompare.currentDateComparison);

  const chartCodeMap = {
    "Brand Sales": "MT",
    "BrandSales": "MT",
    // "BarChartWob": "WOB",
    // "BarChartWobContribution": "GC",
    // "TwoColumnChart": "MBS",
    // "TwoColumnVolumeChart": "MBS",
    "Market Comparison": "MMP",
    "MarketComparisonTable": "MMP",
    "MarketKPIsTable": "MMP",
    "MarketGrowthContribution": "MGCW",
    "MarketContributionSegments": "MCS",
    "NewTable": "MG",
    "SegmentsRetailerValueSalesChart": "SVP"
  };
  
  const [chartData, setChartData] = useState(null);
  
  const [openInsights, setOpenInsights] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsData, setInsightsData] = useState("");
  const [insightsError, setInsightsError] = useState("");

  const handleOpenInsights = async () => {
    setOpenInsights(true);
    setInsightsLoading(true);
    setInsightsError("");
    setInsightsData("");
    
    const cName = user.componentName || user.name;
    const chartCode = chartCodeMap[cName];
    
    if (!chartCode) {
      setInsightsError("Insights not available for this chart type.");
      setInsightsLoading(false);
      return;
    }

    if (!chartData || (Array.isArray(chartData) && chartData.length === 0)) {
      setInsightsError("No data available to generate insights. Please wait for the chart to load.");
      setInsightsLoading(false);
      return;
    }

    const payload = {
        chart_code: chartCode,
        chart_data: chartData
    };

    console.log("Generating insights with NEW payload:", payload);

    try {
      const response = await fetch(`${getUrl()}/market_overview/generate_chart_insights`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      setInsightsData(result.insights || "No insights returned.");
    } catch (err) {
      console.error("Error fetching insights:", err);
      setInsightsError("Failed to fetch insights. Please try again.");
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleCloseInsights = () => {
    setOpenInsights(false);
  };

  // ─── Global dashboard reset ───────────────────────────────────────────────────
  useEffect(() => {
    const handleDashboardReset = () => {
      setSelectedHierarchy(user.filters?.selectedHierarchy || "class");
      setValue(user.filters?.value || 0);
      setIsManufacturer(user.filters?.isManufacturer || false);
      setSelected(user.filters?.selected || { type: "P12M", value: null });
      setBrandSalesTimePeriod(user.filters?.brandSalesTimePeriod || "P12M");
      setBrandSalesSelectedTab(user.filters?.brandSalesSelectedTab || 0);
      setComparisonType(user.filters?.comparisonType || "P12M");
      setTwoColumnSalesType(user.filters?.twoColumnSalesType || null);
      setTwoColumnVolumeSalesType(user.filters?.twoColumnVolumeSalesType || null);
      setSourceShareSelectedSku(user.filters?.sourceShareSelectedSku || "SKU1");
      setPriceElasticitySelectedSkus(user.filters?.priceElasticitySelectedSkus || []);
      setCumulativeProductShareBrand(user.filters?.cumulativeProductShareBrand || "SAVOLA GROUP");
      setCumulativeProductShareStore(user.filters?.cumulativeProductShareStore || "");
      setCumulativeProductShareThreshold(user.filters?.cumulativeProductShareThreshold || 95);
      setPpaSelectedHier(user.filters?.selectedHier || "price_bracket");
      setPpaSelectedType(user.filters?.selectedType || "value_sales");
      setMarketKPIsSelectedHierarchy(user.filters?.marketKPIsSelectedHierarchy || "brand");
      setMarketGrowthSelectedHierarchy(user.filters?.marketGrowthSelectedHierarchy || "brand");
      setMarketGrowthStartDate(
        user.filters?.marketGrowthStartDate
          ? dayjs(user.filters.marketGrowthStartDate)
          : dayjs().subtract(2, "year")
      );
      setMarketGrowthEndDate(
        user.filters?.marketGrowthEndDate
          ? dayjs(user.filters.marketGrowthEndDate)
          : dayjs().subtract(1, "year")
      );
      setMarketGrowthSelectedTab(user.filters?.marketGrowthSelectedTab || 0);
      setMarketGrowthTimePeriod(user.filters?.marketGrowthTimePeriod || "months");
      setMarketGrowthTarget(user.filters?.marketGrowthTarget || 11);
      setMarketGrowthContributionSelectedHierarchy(user.filters?.marketGrowthContributionSelectedHierarchy || "brand");
      setSegmentsRetailerValueSalesSelectedHierarchy(user.filters?.segmentsRetailerValueSalesSelectedHierarchy || "brand");
      setMarketContributionSegmentsSelectedHierarchy(user.filters?.marketContributionSegmentsSelectedHierarchy || "brand");
      setDropDownValue(user.filters?.dropDownValue || "All Market");
      setProductAssortmentSelected(user.filters?.productAssortmentSelected || "SAVOLA GROUP");
      setProductAssortmentSelectedVsHier(user.filters?.productAssortmentSelectedVsHier || "");
      setProductAssortmentSelectedVsHierValue(user.filters?.productAssortmentSelectedVsHierValue || "All");
    };

    window.addEventListener("dashboardFiltersReset", handleDashboardReset);
    return () => window.removeEventListener("dashboardFiltersReset", handleDashboardReset);
  }, [user.filters]);

  // ─── Hierarchy menu data ──────────────────────────────────────────────────────
  useEffect(() => {
    function fetchHierarchyData() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }

      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          return response.json();
        })
        .then((result) => {
          const hierarchyData = result["0"];
          const dynamicMenuConfig = { "All Market": ["All Market"] };
          Object.entries(hierarchyData).forEach(([key, values]) => {
            if (!["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(key)) {
              dynamicMenuConfig[key] = values;
            }
          });
          dynamicMenuConfig["channel"] = [];
          dynamicMenuConfig["retailer"] = [];
          setMenuConfig(dynamicMenuConfig);
        })
        .catch((error) => console.error("Error fetching hierarchy data:", error));
    }

    fetchHierarchyData();
  }, [projectId]);

  useEffect(() => {
    const f = user.filters;
    if (!f) return;

    if (f.selectedHierarchy !== undefined && f.selectedHierarchy !== selectedHierarchy) setSelectedHierarchy(f.selectedHierarchy);
    if (f.value !== undefined && f.value !== value) setValue(f.value);
    if (f.isManufacturer !== undefined && f.isManufacturer !== isManufacturer) setIsManufacturer(f.isManufacturer);
    if (f.selected !== undefined && f.selected !== selected) setSelected(f.selected);
    if (f.brandSalesTimePeriod !== undefined && f.brandSalesTimePeriod !== brandSalesTimePeriod) setBrandSalesTimePeriod(f.brandSalesTimePeriod);
    if (f.brandSalesSelectedTab !== undefined && f.brandSalesSelectedTab !== brandSalesSelectedTab) setBrandSalesSelectedTab(f.brandSalesSelectedTab);
    if (f.comparisonType !== undefined && f.comparisonType !== comparisonType) setComparisonType(f.comparisonType);
    if (f.twoColumnSalesType !== undefined && f.twoColumnSalesType !== twoColumnSalesType) setTwoColumnSalesType(f.twoColumnSalesType);
    if (f.twoColumnVolumeSalesType !== undefined && f.twoColumnVolumeSalesType !== twoColumnVolumeSalesType) setTwoColumnVolumeSalesType(f.twoColumnVolumeSalesType);
    if (f.sourceShareSelectedSku !== undefined && f.sourceShareSelectedSku !== sourceShareSelectedSku) setSourceShareSelectedSku(f.sourceShareSelectedSku);
    if (f.priceElasticitySelectedSkus !== undefined && f.priceElasticitySelectedSkus !== priceElasticitySelectedSkus) setPriceElasticitySelectedSkus(f.priceElasticitySelectedSkus);
    if (f.cumulativeProductShareBrand !== undefined && f.cumulativeProductShareBrand !== cumulativeProductShareBrand) setCumulativeProductShareBrand(f.cumulativeProductShareBrand);
    if (f.cumulativeProductShareStore !== undefined && f.cumulativeProductShareStore !== cumulativeProductShareStore) setCumulativeProductShareStore(f.cumulativeProductShareStore);
    if (f.dropDownValue !== undefined && f.dropDownValue !== dropDownValue) setDropDownValue(f.dropDownValue);
    if (f.marketKPIsSelectedHierarchy && !marketKPIsSelectedHierarchyRef.current && f.marketKPIsSelectedHierarchy !== marketKPIsSelectedHierarchy) setMarketKPIsSelectedHierarchy(f.marketKPIsSelectedHierarchy);
    if (f.marketGrowthContributionSelectedHierarchy !== undefined && f.marketGrowthContributionSelectedHierarchy !== marketGrowthContributionSelectedHierarchy) setMarketGrowthContributionSelectedHierarchy(f.marketGrowthContributionSelectedHierarchy);
    if (f.segmentsRetailerValueSalesSelectedHierarchy !== undefined && f.segmentsRetailerValueSalesSelectedHierarchy !== segmentsRetailerValueSalesSelectedHierarchy) setSegmentsRetailerValueSalesSelectedHierarchy(f.segmentsRetailerValueSalesSelectedHierarchy);
    if (f.marketContributionSegmentsSelectedHierarchy !== undefined && f.marketContributionSegmentsSelectedHierarchy !== marketContributionSegmentsSelectedHierarchy) setMarketContributionSegmentsSelectedHierarchy(f.marketContributionSegmentsSelectedHierarchy);
    if (f.productAssortmentSelected !== undefined && f.productAssortmentSelected !== productAssortmentSelected) setProductAssortmentSelected(f.productAssortmentSelected);
    if (f.productAssortmentSelectedVsHier !== undefined && f.productAssortmentSelectedVsHier !== productAssortmentSelectedVsHier) setProductAssortmentSelectedVsHier(f.productAssortmentSelectedVsHier);
    if (f.productAssortmentSelectedVsHierValue !== undefined && f.productAssortmentSelectedVsHierValue !== productAssortmentSelectedVsHierValue) setProductAssortmentSelectedVsHierValue(f.productAssortmentSelectedVsHierValue);
    if (f.marketGrowthSelectedHierarchy !== undefined && f.marketGrowthSelectedHierarchy !== marketGrowthSelectedHierarchy) setMarketGrowthSelectedHierarchy(f.marketGrowthSelectedHierarchy);
    if (f.marketGrowthSelectedTab !== undefined && f.marketGrowthSelectedTab !== marketGrowthSelectedTab) setMarketGrowthSelectedTab(f.marketGrowthSelectedTab);
    if (f.marketGrowthTimePeriod !== undefined && f.marketGrowthTimePeriod !== marketGrowthTimePeriod) setMarketGrowthTimePeriod(f.marketGrowthTimePeriod);
    if (f.marketGrowthTarget !== undefined && f.marketGrowthTarget !== marketGrowthTarget) setMarketGrowthTarget(f.marketGrowthTarget);
    if (f.marketGrowthStartDate && !dayjs(marketGrowthStartDate).isSame(dayjs(f.marketGrowthStartDate))) setMarketGrowthStartDate(dayjs(f.marketGrowthStartDate));
    if (f.marketGrowthEndDate && !dayjs(marketGrowthEndDate).isSame(dayjs(f.marketGrowthEndDate))) setMarketGrowthEndDate(dayjs(f.marketGrowthEndDate));
  }, [user.filters]); // intentionally watches the whole filters object — see comment above

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: user.id });

  const style = {
    display: "flex",
    flexDirection: "column",
    transition: transition ?? "transform 200ms ease",
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 0 : "auto",
  };

  const componentName = user.componentName || user.name;
  const chartInfo =
    COMPONENT_INFO_MAP[componentName]?.chartInfo ??
    user.component?.type?.chartInfo ??
    null;

  return (
    <Paper
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: 0,
        overflow: "hidden",
        ...(isDragging && {
          border: "2px dashed #00265E",
          backgroundColor: "#eef3fa",
        }),
      }}
      ref={setNodeRef}
      style={style}
      className="user-card"
      elevation={isDragging ? 0 : 3}
    >
      <div
        data-tour="chart-actions"
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {showCloseTooltip ? (
          <Tooltip
            title="You can reopen this chart anytime from 'Add New Chart' above."
            arrow
            placement="top"
          >
            <IconButton data-tour="chart-close" onClick={RemoveComponent} sx={{ mr: 1 }}>
              <CloseOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton data-tour="chart-close" onClick={RemoveComponent} sx={{ mr: 1 }}>
            <CloseOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        <div style={{ display: "flex", alignItems: "center" }}>
          <span data-tour="chart-info">
            <ChartInfoIcon info={chartInfo} iconSize={20} sx={{ p: 0.25, mr: 0.25 }} />
          </span>

          {showInsightsButton && chartCodeMap[user.componentName || user.name] && (
            <Tooltip title="Show Insights" arrow placement="top">
              <IconButton data-tour="chart-insights" onClick={handleOpenInsights}>
                <LightbulbOutlinedIcon sx={{ fontSize: 26, color: "#fbc02d" }} />
              </IconButton>
            </Tooltip>
          )}

          {showSaveButton && (
            <span data-tour="chart-save-canvas">
              <SaveToCanvasButton
                chartData={{
                  id: `${Date.now()}-${Math.floor(Math.random() * 10000)}`,
                  name: user.name,
                  componentName: user.componentName || user.component?.type?.name || "UnknownComponent",
                  xs: user.xs,
                  md: user.md,
                  filters: {},
                }}
                currentFilters={{
                  marketKPIsSelectedHierarchy,
                  selectedHierarchy,
                  value,
                  isManufacturer,
                  selected,
                  brandSalesTimePeriod,
                  brandSalesSelectedTab,
                  comparisonType,
                  twoColumnSalesType: twoColumnSalesTypeRef.current,
                  twoColumnVolumeSalesType: twoColumnVolumeSalesTypeRef.current,
                  sourceShareSelectedSku: sourceShareSelectedSkuRef.current,
                  priceElasticitySelectedSkus: priceElasticitySelectedSkusRef.current,
                  cumulativeProductShareBrand: cumulativeProductShareBrandRef.current,
                  cumulativeProductShareStore: cumulativeProductShareStoreRef.current,
                  cumulativeProductShareThreshold: cumulativeProductShareThresholdRef.current,
                  selectedFilter,
                  currentChartType,
                  currentDateComparison,
                  selectedHier: ppaSelectedHier,
                  selectedType: ppaSelectedType,
                  dropDownValue,
                  productAssortmentSelected: productAssortmentSelectedRef.current,
                  productAssortmentSelectedVsHier: productAssortmentSelectedVsHierRef.current,
                  productAssortmentSelectedVsHierValue: productAssortmentSelectedVsHierValueRef.current,
                  marketGrowthSelectedHierarchy: marketGrowthSelectedHierarchyRef.current,
                  marketGrowthStartDate: marketGrowthStartDateRef.current?.toISOString(),
                  marketGrowthEndDate: marketGrowthEndDateRef.current?.toISOString(),
                  marketGrowthSelectedTab: marketGrowthSelectedTabRef.current,
                  marketGrowthTimePeriod: marketGrowthTimePeriodRef.current,
                  marketGrowthTarget: marketGrowthTargetRef.current,
                  marketGrowthContributionSelectedHierarchy: marketGrowthContributionSelectedHierarchyRef.current,
                  segmentsRetailerValueSalesSelectedHierarchy: segmentsRetailerValueSalesSelectedHierarchyRef.current,
                  marketContributionSegmentsSelectedHierarchy: marketContributionSegmentsSelectedHierarchyRef.current,
                }}
              />
            </span>
          )}

          <Tooltip title="Drag to reorder this chart" arrow placement="top">
            <IconButton
              data-tour="chart-drag"
              {...attributes}
              {...listeners}
              aria-label="drag to reorder chart"
              sx={{ cursor: "grab" }}
            >
              <DragIndicatorIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {user.name === "MarketKPIsTable" || user.componentName === "MarketKPIsTable" ? (
          <MarketKPIsTable
            key={`market-kpis-${user.id}`}
            selectedHierarchy={marketKPIsSelectedHierarchy}
            setSelectedHierarchy={setMarketKPIsSelectedHierarchy}
            onDataLoaded={setChartData}
          />
        ) : user.name === "PPATable" ? (
          <PPATable
            selectedHier={ppaSelectedHier}
            setSelectedHier={setPpaSelectedHier}
            selectedType={ppaSelectedType}
            setSelectedType={setPpaSelectedType}
          />
        ) : user.name === "Brand Sales" ? (
          <BrandSales
            selectedHierarchy={selectedHierarchy}
            setSelectedHierarchy={setSelectedHierarchy}
            timePeriod={brandSalesTimePeriod}
            setTimePeriod={setBrandSalesTimePeriod}
            selectedTab={brandSalesSelectedTab}
            setSelectedTab={setBrandSalesSelectedTab}
            comparisonType={comparisonType}
            setComparisonType={setComparisonType}
            dropDownValue={dropDownValue}
            setDropDownValue={setDropDownValue}
            menuConfig={menuConfig}
            onDataLoaded={setChartData}
          />
        ) : user.name === "Market Comparison" || user.componentName === "MarketComparisonTable" ? (
          <MarketComparisonTable
            comparisonType={comparisonType}
            setComparisonType={setComparisonType}
          />
        ) : user.componentName === "TwoColumnChart" ? (
          <TwoColumnChart
            salesTypeManager={twoColumnSalesType}
            setSalesTypeManager={setTwoColumnSalesType}
            menuConfig={menuConfig}
            salesTypeRef={twoColumnSalesTypeRef}
          />
        ) : user.componentName === "TwoColumnVolumeChart" ? (
          <TwoColumnVolumeChart
            salesTypeManager={twoColumnVolumeSalesType}
            setSalesTypeManager={setTwoColumnVolumeSalesType}
            menuConfig={menuConfig}
            salesTypeRef={twoColumnVolumeSalesTypeRef}
          />
        ) : user.componentName === "SourceShareTable" ? (
          <SourceShareTable
            selectedSku={sourceShareSelectedSku}
            setSelectedSku={setSourceShareSelectedSku}
          />
        ) : user.componentName === "NewTable" ? (
          <NewTable
            selectedHierarchy={marketGrowthSelectedHierarchy}
            setSelectedHierarchy={setMarketGrowthSelectedHierarchy}
            startDate={marketGrowthStartDate}
            setStartDate={setMarketGrowthStartDate}
            endDate={marketGrowthEndDate}
            setEndDate={setMarketGrowthEndDate}
            selectedTab={marketGrowthSelectedTab}
            setSelectedTab={setMarketGrowthSelectedTab}
            timePeriod={marketGrowthTimePeriod}
            setTimePeriod={setMarketGrowthTimePeriod}
            target={marketGrowthTarget}
            setTarget={setMarketGrowthTarget}
            onDataLoaded={setChartData}
          />
        ) : user.componentName === "PriceElasticity" ? (
          <PriceElasticity
            selectedSkus={priceElasticitySelectedSkus}
            setSelectedSkus={setPriceElasticitySelectedSkus}
          />
        ) : user.componentName === "CumulativeProductShareChart" ? (
          <CumulativeProductShareChart
            brandSelection={cumulativeProductShareBrand}
            setBrandSelection={setCumulativeProductShareBrand}
            storeSelection={cumulativeProductShareStore}
            setStoreSelection={setCumulativeProductShareStore}
            thresholdValue={cumulativeProductShareThreshold}
            setThresholdValue={setCumulativeProductShareThreshold}
          />
        ) : user.componentName === "BarChartWob" ? (
          <BarChartWob
            selectedHierarchy={selectedHierarchy}
            setSelectedHierarchy={(newHierarchy) => {
              setSelectedHierarchy(newHierarchy);
              user.onHierarchyChange?.(newHierarchy);
            }}
            value={value}
            setValue={setValue}
            isManufacturer={isManufacturer}
            setIsManufacturer={setIsManufacturer}
            selected={selected}
            setSelected={setSelected}
            selectedFilter={selectedFilter}
            currentChartType={currentChartType}
            currentDateComparison={currentDateComparison}
            projectId={projectId}
          />
        ) : user.componentName === "BarChartWobContribution" ? (
          <BarChartWobContribution
            selectedHierarchy={selectedHierarchy}
            setSelectedHierarchy={(newHierarchy) => {
              setSelectedHierarchy(newHierarchy);
              user.onHierarchyChange?.(newHierarchy);
            }}
            value={value}
            setValue={setValue}
            isManufacturer={isManufacturer}
            setIsManufacturer={setIsManufacturer}
            selected={selected}
            setSelected={setSelected}
            selectedFilter={selectedFilter}
            currentChartType={currentChartType}
            currentDateComparison={currentDateComparison}
            projectId={projectId}
          />
        ) : user.componentName === "MarketGrowthContribution" ? (
          <MarketGrowthContribution
            selectedHierarchy={marketGrowthContributionSelectedHierarchy}
            setSelectedHierarchy={(newHierarchy) => {
              setMarketGrowthContributionSelectedHierarchy(newHierarchy);
              user.onHierarchyChange?.(newHierarchy);
            }}
            onDataLoaded={setChartData}
          />
        ) : user.componentName === "SegmentsRetailerValueSalesChart" ? (
          <SegmentsRetailerValueSalesChart
            selectedHierarchy={segmentsRetailerValueSalesSelectedHierarchy}
            setSelectedHierarchy={(newHierarchy) => {
              setSegmentsRetailerValueSalesSelectedHierarchy(newHierarchy);
              user.onHierarchyChange?.(newHierarchy);
            }}
            onDataLoaded={setChartData}
          />
        ) : user.componentName === "MarketContributionSegments" ? (
          <MarketContributionSegments
            selectedHierarchy={marketContributionSegmentsSelectedHierarchy}
            setSelectedHierarchy={(newHierarchy) => {
              setMarketContributionSegmentsSelectedHierarchy(newHierarchy);
              user.onHierarchyChange?.(newHierarchy);
            }}
            onDataLoaded={setChartData}
          />
        ) : (
          (() => {
            const legacyType = user.component?.type?.name;
            if (legacyType === "SourceShareTable") {
              return <SourceShareTable selectedSku={sourceShareSelectedSku} setSelectedSku={setSourceShareSelectedSku} />;
            }
            if (legacyType === "PriceElasticity") {
              return <PriceElasticity selectedSkus={priceElasticitySelectedSkus} setSelectedSkus={setPriceElasticitySelectedSkus} />;
            }
            if (legacyType === "CumulativeProductShareChart") {
              return (
                <CumulativeProductShareChart
                  brandSelection={cumulativeProductShareBrand}
                  setBrandSelection={setCumulativeProductShareBrand}
                  storeSelection={cumulativeProductShareStore}
                  setStoreSelection={setCumulativeProductShareStore}
                  thresholdValue={cumulativeProductShareThreshold}
                  setThresholdValue={setCumulativeProductShareThreshold}
                />
              );
            }
            if (legacyType === "NewTable") {
              return (
                <NewTable
                  selectedHierarchy={marketGrowthSelectedHierarchy}
                  setSelectedHierarchy={setMarketGrowthSelectedHierarchy}
                  startDate={marketGrowthStartDate}
                  setStartDate={setMarketGrowthStartDate}
                  endDate={marketGrowthEndDate}
                  setEndDate={setMarketGrowthEndDate}
                  selectedTab={marketGrowthSelectedTab}
                  setSelectedTab={setMarketGrowthSelectedTab}
                  timePeriod={marketGrowthTimePeriod}
                  setTimePeriod={setMarketGrowthTimePeriod}
                  target={marketGrowthTarget}
                  setTarget={setMarketGrowthTarget}
                />
              );
            }
            return user.component;
          })()
        )}
      </div>

      <Dialog 
        open={openInsights} 
        onClose={handleCloseInsights} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#00265E',
          color: 'white',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LightbulbOutlinedIcon sx={{ color: "#fbc02d" }} />
            <Typography variant="h6" fontWeight="bold">AI Market Insights</Typography>
          </Box>
          <IconButton onClick={handleCloseInsights} size="small" sx={{ color: "white" }}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: '200px', p: 4, backgroundColor: '#f9f9f9' }}>
          {insightsLoading ? (
            <Box display="flex" flexDirection="column" gap={2} justifyContent="center" alignItems="center" height="100%" flex={1} minHeight="200px">
              <CircularProgress size={40} sx={{ color: '#00265E' }} />
              <Typography variant="body2" color="textSecondary">Generating insights...</Typography>
            </Box>
          ) : insightsError ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
               <Typography color="error" variant="body1" fontWeight="medium">{insightsError}</Typography>
            </Box>
          ) : (
            <Typography sx={{ 
              whiteSpace: "pre-wrap", 
              lineHeight: 1.8, 
              color: '#333', 
              fontSize: '1.05rem',
              letterSpacing: '0.3px',
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
            }}>
              {insightsData.split(/(\b\d+(?:,\d{3})*(?:\.\d+)?\b|'[^']+')/g).map((part, index) => {
                if (/^\d+(?:,\d{3})*(?:\.\d+)?$/.test(part)) {
                  return <span key={index} style={{ color: "#00265E", fontWeight: "bold" }}>{part}</span>;
                }
                if (/^'[^']+'$/.test(part)) {
                  return <strong key={index}>{part.replace(/'/g, "")}</strong>;
                }
                const subParts = part.split(/(\b[A-Z]{3,}(?:\s+[A-Z]{2,})*\b)/g);
                return subParts.map((sub, subIdx) => {
                  if (/^[A-Z]{3,}(?:\s+[A-Z]{2,})*$/.test(sub)) {
                    return <strong key={`${index}-${subIdx}`}>{sub}</strong>;
                  }
                  return sub;
                });
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
          <Button 
            onClick={handleCloseInsights} 
            variant="contained" 
            disableElevation
            sx={{ 
              backgroundColor: '#00265E', 
              color: 'white', 
              borderRadius: 2,
              px: 4,
              textTransform: 'none',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#001a40'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

SortableChart.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    component: PropTypes.node,
  }),
  RemoveComponent: PropTypes.func.isRequired,
};
